/**
 * NOS-217: WCAG-luminance color helpers for the footer chrome.
 *
 * Imported sites bring their own footer background color
 * (`footer_content.background`) — these helpers decide, from that color alone,
 * whether the footer is "dark" (light foreground text) or "light" (dark
 * foreground text) and whether the footer logo needs a contrast chip to stay
 * visible (see src/components/public/FooterLogo.tsx).
 *
 * No dependencies. Parsing covers the color forms the importer and the builder
 * color control produce: #hex (3/4/6/8), rgb[a](), hsl[a](), and a small set
 * of common named colors. Anything unparseable is treated as "not a color" so
 * callers fall back to the default footer background.
 */

export interface RgbColor {
  r: number
  g: number
  b: number
  /** 0–1 */
  a: number
}

/** Common named colors sites actually use for footer backgrounds. */
const NAMED_COLORS: Record<string, string> = {
  white: "#ffffff",
  black: "#000000",
  gray: "#808080",
  grey: "#808080",
  silver: "#c0c0c0",
  whitesmoke: "#f5f5f5",
  gainsboro: "#dcdcdc",
  lightgray: "#d3d3d3",
  lightgrey: "#d3d3d3",
  darkgray: "#a9a9a9",
  darkgrey: "#a9a9a9",
  dimgray: "#696969",
  dimgrey: "#696969",
  navy: "#000080",
  midnightblue: "#191970",
  ivory: "#fffff0",
  beige: "#f5f5dc",
}

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)))
}

function parseHex(hex: string): RgbColor | null {
  const h = hex.replace(/^#/, "")
  if (!/^[0-9a-f]+$/i.test(h)) return null
  if (h.length === 3 || h.length === 4) {
    const r = parseInt(h[0] + h[0], 16)
    const g = parseInt(h[1] + h[1], 16)
    const b = parseInt(h[2] + h[2], 16)
    const a = h.length === 4 ? parseInt(h[3] + h[3], 16) / 255 : 1
    return { r, g, b, a }
  }
  if (h.length === 6 || h.length === 8) {
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
    return { r, g, b, a }
  }
  return null
}

function parseChannel(raw: string): number | null {
  const v = raw.trim()
  if (v.endsWith("%")) {
    const pct = Number(v.slice(0, -1))
    return Number.isFinite(pct) ? clamp255((pct / 100) * 255) : null
  }
  const n = Number(v)
  return Number.isFinite(n) ? clamp255(n) : null
}

function parseAlpha(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === "") return 1
  const v = raw.trim()
  const n = v.endsWith("%") ? Number(v.slice(0, -1)) / 100 : Number(v)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.min(1, n))
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hue = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = l - c / 2
  let rgb: [number, number, number]
  if (hue < 60) rgb = [c, x, 0]
  else if (hue < 120) rgb = [x, c, 0]
  else if (hue < 180) rgb = [0, c, x]
  else if (hue < 240) rgb = [0, x, c]
  else if (hue < 300) rgb = [x, 0, c]
  else rgb = [c, 0, x]
  return { r: clamp255((rgb[0] + m) * 255), g: clamp255((rgb[1] + m) * 255), b: clamp255((rgb[2] + m) * 255) }
}

const RGB_RE = /^rgba?\(\s*([\d.]+%?)\s*[,\s]\s*([\d.]+%?)\s*[,\s]\s*([\d.]+%?)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i
const HSL_RE = /^hsla?\(\s*([\d.-]+)(?:deg)?\s*[,\s]\s*([\d.]+)%\s*[,\s]\s*([\d.]+)%\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i

/**
 * Parse a CSS color string into RGBA. Returns null for anything that is not a
 * plain color (gradients, var(), keywords like transparent/inherit, junk).
 */
export function parseCssColor(input: string | null | undefined): RgbColor | null {
  if (typeof input !== "string") return null
  const v = input.trim().toLowerCase().replace(/\s*!important\s*$/, "")
  if (v === "") return null
  const named = NAMED_COLORS[v]
  if (named) return parseHex(named)
  if (v.startsWith("#")) return parseHex(v)
  const rgbMatch = v.match(RGB_RE)
  if (rgbMatch) {
    const r = parseChannel(rgbMatch[1])
    const g = parseChannel(rgbMatch[2])
    const b = parseChannel(rgbMatch[3])
    const a = parseAlpha(rgbMatch[4])
    if (r == null || g == null || b == null || a == null) return null
    return { r, g, b, a }
  }
  const hslMatch = v.match(HSL_RE)
  if (hslMatch) {
    const h = Number(hslMatch[1])
    const s = Number(hslMatch[2]) / 100
    const l = Number(hslMatch[3]) / 100
    const a = parseAlpha(hslMatch[4])
    if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l) || a == null) return null
    return { ...hslToRgb(h, s, l), a }
  }
  return null
}

/** WCAG 2.x relative luminance (0 = black, 1 = white) from 0–255 channels. */
export function relativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number): number => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** Relative luminance of a CSS color string, or null when unparseable. */
export function luminanceOf(color: string | null | undefined): number | null {
  const rgb = parseCssColor(color)
  if (!rgb) return null
  return relativeLuminance(rgb.r, rgb.g, rgb.b)
}

/**
 * The luminance at which white and black text give equal WCAG contrast
 * (sqrt(1.05 * 0.05) - 0.05). Below it, white foreground wins.
 */
export const WHITE_TEXT_LUMINANCE_THRESHOLD = 0.179

/**
 * True when a background color is "dark" — i.e. white/light foreground text
 * has better WCAG contrast than black/dark text. Unparseable colors return
 * null so the caller can fall back to its default.
 */
export function isDarkBackground(color: string | null | undefined): boolean | null {
  const l = luminanceOf(color)
  if (l == null) return null
  return l < WHITE_TEXT_LUMINANCE_THRESHOLD
}

/** Client-side sampled classification of a logo image (see useImageLuminance). */
export type ImageLuminance = "light" | "dark" | "unknown"

/**
 * Mean relative luminance over the non-transparent pixels of an RGBA byte
 * array (canvas ImageData.data layout). Null when every pixel is transparent.
 */
export function averageLuminanceOfPixels(data: ArrayLike<number>): number | null {
  let sum = 0
  let count = 0
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 32) continue // ignore (near-)transparent pixels
    sum += relativeLuminance(data[i], data[i + 1], data[i + 2])
    count++
  }
  return count === 0 ? null : sum / count
}

/** Classify a sampled mean luminance into light/dark. */
export function classifyLuminance(mean: number | null): ImageLuminance {
  if (mean == null) return "unknown"
  return mean >= 0.5 ? "light" : "dark"
}

/**
 * Whether an RGBA byte array has enough transparent pixels for a silhouette
 * CSS filter (knockout) to make sense. Opaque images (JPEG, flattened PNG)
 * must keep the chip treatment — a filter would flood their whole rectangle.
 * Threshold: >2% of sampled pixels meaningfully transparent.
 */
export function hasMeaningfulAlpha(data: ArrayLike<number>): boolean {
  let transparent = 0
  let total = 0
  for (let i = 0; i + 3 < data.length; i += 4) {
    total++
    if (data[i + 3] < 250) transparent++
  }
  return total > 0 && transparent / total > 0.02
}

/**
 * NOS-217 logo-visibility decision: wrap the footer logo in a contrasting chip
 * when the logo would collide with the footer background.
 *
 * - A user-set footer logo override (footer_content.logo_url, PR #240) is
 *   always rendered untouched — the user picked that file for this footer.
 * - Dark background: chip unless the logo is verifiably light. "Unknown"
 *   (sampling impossible: cross-origin SVG, no canvas) gets the chip because
 *   imported org logos are overwhelmingly dark wordmarks — the exact
 *   dark-on-dark failure this issue fixes.
 * - Light background: chip only when the logo is verifiably light
 *   (light-on-light); unknown logos render bare, matching the historical
 *   assumption that logos are dark marks.
 */
export function needsLogoChip(
  backgroundIsDark: boolean,
  logoLuminance: ImageLuminance,
  isUserOverride: boolean
): boolean {
  if (isUserOverride) return false
  if (backgroundIsDark) return logoLuminance !== "light"
  return logoLuminance === "light"
}
